import { Close } from '@mui/icons-material';
import { TabContext, TabList } from '@mui/lab';
import { IconButton, Stack, Tab, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  setActiveWindow,
  setTransportSelectedItem,
  setTransportSelectedItemConditionOptions,
  setTransportTab,
  setTransportWindowMinimize,
} from 'app/actions/map';
import { MaximizeIcon, MinimizeIcon } from 'app/icons';
import { MapTransportPicker } from 'app/pages/Map';
import TabPanelBroadcast from 'app/pages/Map/MapTransportWindow/TabPanelBroadcast';
import TabPanelChart from 'app/pages/Map/MapTransportWindow/TabPanelChart';
import TabPanelCondition from 'app/pages/Map/MapTransportWindow/TabPanelCondition/TabPanelCondition';
import TabPanelEvent from 'app/pages/Map/MapTransportWindow/TabPanelEvent';
import TabPanelFlight from 'app/pages/Map/MapTransportWindow/TabPanelFlight';
import TabPanelInfo from 'app/pages/Map/MapTransportWindow/TabPanelInfo';
import { useAsyncAction, useInterval, usePermission, useRoles } from 'app/services';
import { transportBroadcastLoad, transportCommandSend, transportConditionLoad, transportTaskInfoLoad } from 'app/store/map';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { useDispatch, useSelector } from 'react-redux';

const MapTransportWindow = () => {
  const dispatch = useDispatch();
  const guardPermission = usePermission('guard');
  const sensorPermission = usePermission('sensor');
  const fuelLevelPermission = usePermission('fuel_level');
  const isSuperAdmin = useRoles([config.userRoles['SUPERADMIN']]);

  const { activeWindow, transportWindowMinimize, transportTab } = useSelector((state) => state.map);
  const { transportSelectedItem, transportSelectedDate, transportConditionSelectedItem } = useSelector((state) => state.map);
  const { asyncAction: commandSubmit } = useAsyncAction(transportCommandSend);
  const { asyncAction: fetchConditionData, isLoading: isFetchConditionLoading } = useAsyncAction(transportConditionLoad, [
    transportSelectedItem?.id,
  ]);
  const { asyncAction: fetchBroadcastData, isLoading: isFetchBroadcastLoading } = useAsyncAction(transportBroadcastLoad, [
    transportSelectedItem?.id,
  ]);
  const { asyncAction: fetchTaskInfoData } = useAsyncAction(transportTaskInfoLoad);
  const [fetchStatuses, setFetchStatuses] = useState([]);
  const [datetimeInterval, setDatetimeInterval] = useState(moment());
  const transportConditionListRef = useRef();

  const transportConditionItems = useMemo(() => {
    if (transportSelectedItem) {
      return transportSelectedItem.conditions.filter((condition) => condition.type === 'message');
    }

    return [];
  }, [transportSelectedItem]);

  const handleActiveWindow = () => dispatch(setActiveWindow('transport'));

  const handleWindowCloseClick = () => dispatch(setTransportSelectedItem(null));

  const handleMinimizeClick = () => dispatch(setTransportWindowMinimize(!transportWindowMinimize));

  const handleTabChange = (event, value) => dispatch(setTransportTab(value));

  const handleConditionOptionsOpenClick = () =>
    dispatch(
      setTransportSelectedItemConditionOptions({
        ...transportSelectedItem.conditionOptions,
        open: !transportSelectedItem.conditionOptions.open,
      })
    );

  const handleConditionGuardOutClick = (number) => {
    commandSubmit(transportSelectedItem.id, `GC ${number}`);
  };

  const handleLogKey = (e) => {
    switch (e.code) {
      case 'ArrowUp':
        e.preventDefault();
        transportConditionListRef.current?.handleKeyUp();
        break;
      case 'ArrowDown':
        e.preventDefault();
        transportConditionListRef.current?.handleKeyDown();
        break;
    }
  };

  const declOfNum = (number, titles) => {
    return titles[number % 100 > 4 && number % 100 < 20 ? 2 : [2, 0, 1, 1, 1, 2][number % 10 < 5 ? number % 10 : 5]];
  };

  useInterval(() => {
    if (moment().seconds() === 0) {
      setDatetimeInterval(moment());
    }
  }, 1000);

  useEffect(() => {
    setFetchStatuses([]);
  }, [transportSelectedItem?.id, transportSelectedDate]);

  useEffect(() => {
    if (fetchStatuses.includes(transportTab)) {
      return;
    }

    switch (transportTab) {
      case 'info':
        fetchTaskInfoData(transportSelectedItem.id);
        break;
      case 'broadcast':
        if (config.transportDeviceTypes[transportSelectedItem.device_type] === 'galileosky') {
          fetchBroadcastData(transportSelectedItem.id);
        }
        break;
      case 'flight':
      case 'chart':
      case 'condition':
      case 'event':
        if (!fetchStatuses.some((status) => ['flight', 'chart', 'condition', 'event'].includes(status))) {
          fetchConditionData(transportSelectedItem.id);
        }
        break;
    }

    setFetchStatuses([...fetchStatuses, transportTab]);
  }, [transportTab, fetchStatuses]);

  useEffect(() => {
    document.addEventListener('keydown', handleLogKey);

    return () => {
      document.removeEventListener('keydown', handleLogKey);
    };
  }, []);

  if (!transportSelectedItem) {
    return <React.Fragment />;
  }

  return (
    <Draggable bounds='parent' handle='.header-draggable' disabled={transportWindowMinimize}>
      <Stack
        direction='column'
        onClick={handleActiveWindow}
        sx={(theme) => ({
          position: 'absolute',
          right: '80px',
          top: theme.spacing(4),
          minWidth: '804px',
          maxWidth: '804px',
          height: transportTab === 'chart' ? '923px' : '678px',
          maxHeight: transportWindowMinimize ? '678px' : '100%',
          zIndex: '1050',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 1px 3px ' + alpha(theme.palette.grey.main, 0.12),
          borderRadius: theme.shape.borderRadius,
          transform: 'translate(0px, 0px)',
          '.header-draggable': {
            width: '100%',
            cursor: 'grab',
            '.MuiOutlinedInput-input': {
              padding: theme.spacing(0),
            },
            '.MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '.MuiInputAdornment-positionEnd': {
              margin: theme.spacing(0),
            },
          },
          '&.react-draggable-dragging .header-draggable': {
            cursor: 'grabbing',
          },
          ...(transportWindowMinimize && {
            top: 'auto',
            right: '0',
            bottom: '0',
            width: 'auto',
            minWidth: '300px',
            height: '100%',
            transform: 'translateY(634px) !important',
            borderTopRightRadius: '0',
            borderBottomLeftRadius: '0',
            borderBottomRightRadius: '0',
            '.header-draggable': {
              cursor: 'default',
            },
          }),
          ...(activeWindow === 'transport' && {
            zIndex: '1051',
          }),
        })}>
        <TabContext value={transportTab}>
          <Stack
            direction='row'
            sx={(theme) => ({
              userSelect: 'none',
              bgcolor: 'background.default',
              '.MuiAvatar-root': {
                bgcolor: alpha(theme.palette.primary.main, 0.12),
              },
              '.MuiTypography-root': {
                fontSize: '13px',
              },
              '.header-draggable': {
                width: '100%',
                minHeight: '44px',
                padding: '10px 10px 10px 10px',
                '.MuiTypography-root': {
                  padding: theme.spacing(0, 2),
                },
                ...(transportWindowMinimize && {
                  paddingLeft: '0',
                }),
              },
            })}>
            <Stack
              direction='row'
              alignItems='center'
              component='ul'
              sx={(theme) => ({
                left: '0',
                top: '9px',
                marginRight: 'auto',
                padding: '0',
                listStyle: 'none',
                margin: theme.spacing(0, 2),
                li: {
                  margin: '0',
                  padding: '0',
                },
                ...(transportWindowMinimize && {
                  display: 'none',
                }),
              })}>
              <li>
                <MapTransportPicker />
              </li>
            </Stack>
            <Stack direction='row' justifyContent='start' alignItems='center' className='header-draggable'>
              <Typography variant='h6' component='span'>
                {transportSelectedItem.name}
              </Typography>
            </Stack>
            <Stack
              direction='row'
              alignItems='center'
              component='ul'
              sx={(theme) => ({
                position: 'absolute',
                right: '8px',
                top: '7px',
                margin: '0',
                marginLeft: 'auto',
                listStyle: 'none',
                paddingLeft: 0,
                li: {
                  margin: '0',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  button: {
                    width: '30px',
                    height: '30px',
                    '.maximizeIcon, .minimizeIcon': {
                      width: '16px',
                      height: '16px',
                    },
                  },
                },
                'li:not(:last-of-type) path': {
                  fill: theme.palette.text.secondary,
                },
              })}>
              <li>
                <IconButton color='default' onClick={handleMinimizeClick}>
                  {transportWindowMinimize && <MaximizeIcon className='maximizeIcon' fontSize='small' />}
                  {!transportWindowMinimize && <MinimizeIcon className='minimizeIcon' fontSize='small' />}
                </IconButton>
              </li>
              <li>
                <IconButton color='default' onClick={handleWindowCloseClick}>
                  <Close fontSize='small' />
                </IconButton>
              </li>
            </Stack>
          </Stack>
          <Stack
            direction='row'
            alignItems='center'
            sx={{
              ...(isFetchConditionLoading && {
                borderColor: 'transparent',
              }),
              ...(transportTab === 'info' && {
                borderBottom: '0',
                '.MuiInput-root': {
                  opacity: '0',
                  visibility: 'hidden',
                },
              }),
              ...(transportWindowMinimize && {
                width: '0px !important',
                opacity: '0',
                visibility: 'hidden',
              }),
            }}>
            <TabList
              onChange={handleTabChange}
              TabIndicatorProps={{ children: <span /> }}
              sx={(theme) => ({
                width: '100%',
                minHeight: '44px',
                '.MuiTab-root': {
                  fontSize: '12px',
                  minHeight: '44px',
                  padding: theme.spacing(2),
                  '&.Mui-selected': {
                    color: 'inherit',
                  },
                },
                '& .MuiTabs-indicator': {
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: 'transparent',
                },
                '& .MuiTabs-indicator span': {
                  width: 'calc(100% - ' + theme.spacing(4) + ')',
                  bgcolor: theme.palette.primary.main,
                  borderRadius: theme.shape.borderRadius,
                },
              })}>
              <Tab value='info' label='Информация' disableRipple />
              <Tab value='broadcast' label='Видео' disableRipple />
              <Tab value='flight' label='Рейсы' disableRipple />
              <Tab value='chart' label='Графики' disableRipple />
              <Tab value='condition' label='Состояния' disableRipple />
              <Tab value='event' label='Журнал событий' disableRipple />
            </TabList>
          </Stack>
          <Stack
            direction='row'
            sx={(theme) => ({
              display: 'none',
              borderBottom: '1px solid',
              borderColor: theme.palette.divider,
              padding: '4px 12px',
              '.MuiButtonBase-root': {
                bgcolor: 'background.default',
                '&.Mui-disabled': {
                  bgcolor: 'background.default',
                },
              },
              ...(transportWindowMinimize && {
                width: '0px !important',
                opacity: '0',
                visibility: 'hidden',
              }),
              ...(transportTab === 'info' && {
                display: 'none',
              }),
            })}></Stack>
          <Stack
            direction='row'
            sx={(theme) => ({
              flex: '1 1 auto',
              display: 'flex',
              overflow: 'hidden',
              '.MuiTabPanel-root': {
                width: '100%',
                height: '100%',
                padding: theme.spacing(0),
              },
              ...(transportWindowMinimize && {
                width: '0px',
                opacity: '0',
                visibility: 'hidden',
              }),
            })}>
            <React.Fragment>
              <TabPanelInfo transportSelectedItem={transportSelectedItem} />
              <TabPanelFlight transportSelectedItem={transportSelectedItem} isFetchConditionLoading={isFetchConditionLoading} />
              <TabPanelBroadcast transportSelectedItem={transportSelectedItem} isFetchBroadcastLoading={isFetchBroadcastLoading} />
              <TabPanelChart
                isFetchConditionLoading={isFetchConditionLoading}
                transportConditionItems={transportConditionItems}
                transportSelectedItem={transportSelectedItem}
              />
              <TabPanelCondition
                transportConditionItems={transportConditionItems}
                isFetchConditionLoading={isFetchConditionLoading}
                handleConditionOptionsOpenClick={handleConditionOptionsOpenClick}
                transportSelectedItem={transportSelectedItem}
                transportSelectedDate={transportSelectedDate}
                guardPermission={guardPermission}
                fuelLevelPermission={fuelLevelPermission}
                relayPermission={isSuperAdmin}
                handleConditionGuardOutClick={handleConditionGuardOutClick}
                transportConditionSelectedItem={transportConditionSelectedItem}
                declOfNum={declOfNum}
                datetimeInterval={datetimeInterval}
                sensorPermission={sensorPermission}
                transportConditionListRef={transportConditionListRef}
              />
              <TabPanelEvent isFetchConditionLoading={isFetchConditionLoading} transportSelectedItem={transportSelectedItem} />
            </React.Fragment>
          </Stack>
        </TabContext>
      </Stack>
    </Draggable>
  );
};

export default MapTransportWindow;
